import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReportingUserSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-user-sessions.service';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import * as ConversationQueueAction from '@nimbus-portal/common/store/conversation-queue/conversation-queue.actions';

const PENDING_CONTEXT_LINK_TIMEOUT_MS = 30_000;

interface IPendingContextLink {
  teamId: string;
  serviceSessionId: string;
}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class PickupContextLinksService {
  private readonly pendingLinks = new Map<string, IPendingContextLink>();

  public constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly reportingUserSessionsService: ReportingUserSessionsService,
  ) {}

  public initialize(): void {
    this.actions$
      .pipe(ofActionDispatched(ConversationQueueAction.Pickup), untilDestroyed(this))
      .subscribe(({ conversationId, teamId, serviceSessionId }: ConversationQueueAction.Pickup) => {
        this.pendingLinks.set(conversationId, { teamId, serviceSessionId });
        setTimeout(() => this.pendingLinks.delete(conversationId), PENDING_CONTEXT_LINK_TIMEOUT_MS);
      });

    this.reportingUserSessionsService
      .getUserSessionsLive$()
      .pipe(untilDestroyed(this))
      .subscribe((sessions) => this.tryOpenPendingLinks(sessions));
  }

  private tryOpenPendingLinks(sessions: IUserSessionLive[]): void {
    if (!this.pendingLinks.size) {
      return;
    }
    for (const session of sessions) {
      const pending = this.pendingLinks.get(session.conversationId);
      if (pending && session.id) {
        this.pendingLinks.delete(session.conversationId);
        this.store.dispatch(
          new ConversationQueueAction.OpenCrmLinks(session.conversationId, pending.teamId, pending.serviceSessionId, session.id),
        );
      }
    }
  }
}
